<template>
    <div class="products-list">
        <div class="products-list__wrapper">
            <div class="products-list__top-title-wrapper">{{ $t('dashboard.moreMoney') }}</div>
            <div class="products-list__top-description-wrapper">{{ $t('dashboard.moreMoneyInfo') }}</div>
            <KLoader v-if="loading" />
            <div v-if="!loading && items.length > 0" class="products-list__main-wrapper">
                <div v-for="(item, index) in items" :key="item.id" class="products-list__item-wrapper" @click="goToArticle(item.article_id)">
                    <div class="products-list__item-top-wrapper">
                        <div class="products-list__item-index">{{ index + 1 }}</div>
                        <img :src="item.url" alt="kasanowa" />
                    </div>
                    <div class="products-list__description-wrapper">
                        <div class="products-list__name">{{ item.name }}</div>
                        <KButton
                            v-if="item.article_id != null"
                            :id="`product-${index}`"
                            :label="$t('products.more')"
                            type="submit"
                            uppercase
                            white
                            icon="arrowRight"
                            :color="black"
                        />
                    </div>
                </div>
            </div>
            <div class="products-list__bottom-wrapper">
                <div class="products-list__bottom-icon kasanowa-desktop">!</div>
                <div class="products-list__title">{{ $t('products.moneyWaiting') }}</div>
                <div class="products-list__info-wrapper">
                    <div class="products-list__info-text-wrapper">
                        <div>{{ $t('products.inChallenge') }} {{ challengeName }} {{ $t('products.youEarned') }} <span>{{ challengePrice }} PLN</span>.</div>
                        <div>{{ $t('products.youEarnedInfo') }}</div>
                    </div>
                    <div>
                        <KButton
                            id="earn-more"
                            :label="$t('products.earnMore')"
                            type="submit"
                            uppercase
                            white
                            icon="arrowRight"
                            :color="black"
                            @click="navigateTo('/quiz-ph')"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from '@vue/runtime-core';
import { navigateTo } from '@/hooks/NavigateHooks';
import ProductApi from '@/api/v1/ProductApi';
import { toast } from 'vue3-toastify';
import variables from '@/assets/scss/modules/variables.module.scss';
import type { IProductList } from '@/models/ProductsModel';
import KButton from '@/components/atoms/KButton.vue';
import KLoader from '@/components/atoms/KLoader.vue';

const { black } = variables;

const challengeName = ref<string>('');
const challengePrice = ref<string>('');
const items = ref<IProductList[]>([]);

const loading = ref<boolean>(false);
const productApi = new ProductApi();
const productList = async() => {
    loading.value = true;
    try {
        const result = await productApi.productsList();
        if (!result.isSuccess) {
            toast.error(t('products.toast.errorGetProductList'));
            loading.value = false;
            return;
        }
        items.value = result.payload.products;
        challengeName.value = result.payload.name;
        challengePrice.value = result.payload.money_get || '0.00';
    } catch (e) {
        toast.error(t('products.toast.errorGetProductList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
productList();

const goToArticle = (articleId: number | null) => {
    if (articleId != null) {
        navigateTo(`/articles-ph/${articleId}`)
    }
};
</script>

<style scoped lang="scss">
.products-list {
    margin-top: 80px;

    @include media-breakpoint-up(lg) {
        margin-top: 240px;
    }

    &__wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        border: 1px solid $secondary-400;
        background: rgba($primary-400, 0.25);
        backdrop-filter: blur(2px) saturate(150%);
        -webkit-backdrop-filter: blur(5px) saturate(150%);
        padding: 140px 16px 16px;
    }

    &__top-title-wrapper {
        position: absolute;
        top: -40px;
        left: 50%;
        transform: translateX(-50%);
        background-color: $secondary-400;
        font-weight: 900;
        color: $black;
        font-size: 26px;
        padding: 0 14px;
        letter-spacing: 0;
        width: 272px;
        text-transform: uppercase;
        text-align: center;

        @include media-breakpoint-up(lg) {
            font-size: 52px;
            padding: 0 15px;
            width: max-content;
            top: -40px;
        }
    }

    &__top-description-wrapper {
        position: absolute;
        top: 40px;
        left: 50%;
        transform: translateX(-50%);
        font-weight: 600;
        color: $white;
        font-size: 16px;
        padding: 0 14px;
        letter-spacing: 0;
        width: 100%;
        text-align: center;

        @include media-breakpoint-up(lg) {
            font-size: 18px;
            padding: 0 15px;
            width: max-content;
            top: 60px;
        }
    }

    &__main-wrapper {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 32px + 30px;

        @include media-breakpoint-up(lg) {
            gap: 32px + 50px;
        }
    }

    &__item-wrapper {
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 100%;
        max-width: 300px;
        cursor: pointer;

        @include media-breakpoint-up(xxl) {
            width: 350px;
            max-width: 350px;
        }

        &:hover {
            .products-list__item-index {
                background-color: $secondary-400;
            }

            img {
                border-color: $secondary-400;
                transform: scale(1.03);
            }

            button {
                background-color: $secondary-400;
            }
        }
    }

    &__item-top-wrapper {
        position: relative;
        max-width: 300px;
        max-height: 225px;
        width: 100%;
        height: 100%;

        @include media-breakpoint-up(xxl) {
            max-width: 350px;
            max-height: 300px;
            width: 350px;
            height: 300px;
        }

        img {
            max-width: 300px;
            max-height: 225px;
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 10px;
            border: 1px solid $primary-400;
            transition: 0.3s ease-in-out;

            @include media-breakpoint-up(sm) {
                max-width: 300px;
                max-height: 225px;
                width: 300px;
                height: 225px;
            }

            @include media-breakpoint-up(xxl) {
                max-width: 350px;
                max-height: 300px;
                width: 350px;
                height: 300px;
            }
        }
    }

    &__item-index {
        position: absolute;
        z-index: 1;
        top: -28px;
        left: 50%;
        transform: translateX(-50%);
        background-color: $primary-400;
        color: $black;
        width: 55px;
        height: 55px;
        border-radius: 50%;
        font-size: 24px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: 0.3s ease-in-out;

        @include media-breakpoint-up(lg) {
            width: 73px;
            height: 73px;
            font-size: 32px;
            top: -37px;
        }
    }

    &__description-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            justify-content: space-between;
        }
    }

    &__name {
        font-size: 14px;
        font-weight: 700;
        text-align: center;
        color: $white;

        @include media-breakpoint-up(sm) {
            white-space: nowrap;      // Zapobiega łamaniu tekstu na nowe linie
            overflow: hidden;         // Ukrywa nadmiarowy tekst
            text-overflow: ellipsis;  // Pokazuje "..." gdy tekst jest za długi
            max-width: 100%;
        }
    }

    &:deep(.k-btn) {
        transition: 0.3s ease-in-out;
    }

    &__bottom-wrapper {
        display: flex;
        flex-direction: column;
        position: relative;
        width: 100%;
        background-color: transparent;
        border-radius: 10px;
        padding: 45px 15px 25px;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 10px;

        @include media-breakpoint-up(lg) {
            margin-top: 60px;
            background: rgba($secondary-400, 0.15);
            backdrop-filter: blur(2px) saturate(150%);
            -webkit-backdrop-filter: blur(5px) saturate(150%);
        }
    }

    &__bottom-icon {
        position: absolute;
        z-index: 2;
        top: -37px;
        left: 50%;
        transform: translateX(-50%);
        background-color: $red-400;
        border-radius: 50%;
        width: 73px;
        height: 73px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $white;
        font-size: 32px;
        font-weight: 900;
    }

    &__title {
        font-size: 20px;
        color: $white;
        font-weight: 900;
        letter-spacing: 0;
        text-transform: uppercase;

        @include media-breakpoint-up(lg) {
            font-size: 52px;
        }
    }

    &__info-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            gap: 50px;
        }
    }

    &__info-text-wrapper {
        display: flex;
        flex-direction: column;
        text-align: center;
        color: $white;

        @include media-breakpoint-up(lg) {
            text-align: left;
        }

        span {
            color: $secondary-400;
            font-weight: 700;
        }
    }
}
</style>
